import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const SLIDES = [
  {
    num: "01",
    tag: "Staff Apparel",
    title: "Sartorialità in Sala",
    img: "/img/club/club-polo.jpg",
    alt: "Polo sartoriale nera logata con crest oro per il personale di sala",
    text: "Con l'ordine minimo d'allocazione, forniamo fino a 4 polo/magliette sartoriali logate per il personale di sala. Eleganti, sobrie e perfette per coordinare lo stile del servizio.",
  },
  {
    num: "02",
    tag: "Accessori",
    title: "Corredo di Servizio d'Élite",
    img: "/img/club/club-kit.jpg",
    alt: "Glacette nera logata con bottiglia Blanche nel ghiaccio e cavatappi da sommelier nero e oro",
    text: "Ricevi il kit ufficiale da servizio: il secchiello porta-ghiaccio (glacette) logato e il classico cavatappi da sommelier nero e oro con il crest della Maison.",
  },
  {
    num: "03",
    tag: "Limited Edition",
    title: "Targa Riconoscimento d'Allocazione",
    img: "/img/club/club-targa.jpg",
    alt: "Targa d'allocazione in plexiglass nero lucido con crest e scritte incise in oro",
    text: "Allocando le cuvée rare numerate (Blanche e Claire), il locale riceve la Targa autoportante in plexiglass nero lucido con incisioni in oro, che certifica la presenza di micro-lotti introvabili nella tua Carta dei Vini.",
  },
  {
    num: "04",
    tag: "Accesso Riservato",
    title: "Priority Allocation & Concierge",
    img: "/img/club/club-concierge.jpg",
    alt: "Invito Masterclass in carta materica con finiture oro a caldo e busta coordinata",
    text: "Accesso diretto al Concierge Digitale B2B per riordini prioritari prima dell'esaurimento annata, schede tecniche riservate e l'invito ufficiale in carta materica alle Masterclass d'Autore.",
  },
];

function ClubNum({ i, idx, num }) {
  const color = useTransform(idx, (v) =>
    Math.abs(v - i) < 0.5 ? "#C4A671" : "#C9C3B5"
  );
  const scale = useTransform(idx, (v) => (Math.abs(v - i) < 0.5 ? 1.15 : 1));
  return (
    <motion.span
      className="font-serif-display italic text-xl"
      style={{ color, scale }}
      data-testid={`club-num-${i}`}
    >
      {num}
    </motion.span>
  );
}

function ClubCard({ s, i, idx, shift }) {
  const x = useTransform(idx, (v) => (i - v) * shift);
  const rotateY = useTransform(idx, (v) => Math.max(-24, Math.min(24, (i - v) * -18)));
  const scale = useTransform(idx, (v) => 1 - Math.min(0.16, Math.abs(v - i) * 0.16));
  const opacity = useTransform(idx, (v) => Math.max(0, 1 - Math.abs(v - i) * (shift < 420 ? 1.15 : 0.72)));
  const zIndex = useTransform(idx, (v) => 20 - Math.round(Math.abs(v - i) * 5));

  return (
    <motion.div
      style={{ x, rotateY, scale, opacity, zIndex }}
      className="absolute inset-0 flex justify-center will-change-transform"
      data-testid={`partner-slide-${i}`}
    >
      <div
        className="w-full max-w-[430px] bg-[#0A0A0A] overflow-hidden"
        style={{ boxShadow: "0 0 0 1px rgba(196,166,113,0.4), 0 30px 70px rgba(10,10,10,0.35)" }}
      >
        <div className="relative overflow-hidden">
          <img
            src={s.img}
            alt={s.alt}
            loading="lazy"
            className="w-full aspect-[3/2] object-cover"
            data-testid={`partner-slide-img-${i}`}
          />
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(to top, rgba(10,10,10,0.55), transparent 55%)" }}
          />
          <span className="absolute bottom-4 left-5 font-serif-display italic text-[#C4A671] text-2xl">
            {s.num}
          </span>
        </div>
        <div className="p-6 sm:p-8">
          <div className="overline text-[9px] text-[#8B7A55]">{s.tag}</div>
          <h3 className="font-serif-display text-[21px] sm:text-[23px] leading-tight text-[#F2EFE8] mt-2.5">
            {s.title}
          </h3>
          <p className="mt-4 text-[13px] sm:text-[13.5px] leading-[1.8] text-[#C9C3B5] font-light">
            {s.text}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function PartnerCarousel() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });
  const idx = useTransform(scrollYProgress, [0.08, 0.92], [0, 3], { clamp: true });
  const shift = typeof window !== "undefined" && window.innerWidth < 640 ? 330 : 480;

  return (
    <div ref={wrapRef} className="relative h-[280vh] mt-16 sm:mt-24" data-testid="partner-carousel">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden pt-16 sm:pt-0">
        <div className="flex items-center gap-6 mb-8">
          <div className="h-px w-12 bg-[#C4A671]" />
          <span className="overline text-[10px] text-[#8B7A55]">
            Il Corredo del Partner d'Allocazione
          </span>
          <div className="h-px flex-1 bg-[#EAEAEA]" />
          <div className="hidden sm:flex items-center gap-5">
            {SLIDES.map((s, i) => (
              <ClubNum key={i} i={i} idx={idx} num={s.num} />
            ))}
          </div>
        </div>

        {/* Palcoscenico 3D: le schede scorrono con la rotellina */}
        <div
          className="relative h-[500px] sm:h-[560px]"
          style={{ perspective: "1300px" }}
          data-testid="partner-stage"
        >
          {SLIDES.map((s, i) => (
            <ClubCard key={i} s={s} i={i} idx={idx} shift={shift} />
          ))}
        </div>

        <p className="sm:hidden mt-2 text-center overline text-[9px] text-[#8B7A55]">
          Continua a scorrere per sfogliare il corredo
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() =>
              document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" })
            }
            className="px-9 py-4 bg-[#0A0A0A] text-[#F2EFE8] overline text-[10.5px] tracking-[0.2em] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition-colors duration-300"
            data-testid="partner-cta-allocation"
          >
            Richiedi l'Allocazione / Diventa Partner →
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("vdi-open-chat"))}
            className="btn-ghost"
            data-testid="partner-cta-concierge"
          >
            Parla col Concierge
          </button>
        </div>
      </div>
    </div>
  );
}
